// Level definitions for Chronos - Le Défi des Époques
// Each level defines objects, slots, causal rules, and win conditions.
// Rules are evaluated forward: when a source condition becomes true, the target state is applied.

export const ERAS = {
  antiquity:   { key: "antiquity",   label: "Antiquité Égyptienne", tint: "antiquity",   icon: "🜃" },
  medieval:    { key: "medieval",    label: "Moyen-Âge",             tint: "medieval",    icon: "⚔" },
  renaissance: { key: "renaissance", label: "Renaissance",           tint: "renaissance", icon: "🎭" },
  future:      { key: "future",      label: "Futur Cybernétique",    tint: "future",      icon: "◈" },
};

export const LEVELS = [
  {
    id: 1,
    title: "L'Éveil du Nil",
    subtitle: "Chapitre I · La première fissure",
    hint_discovery: "Place la braise orange dans la forge médiévale. Regarde ce qui se passe dans le futur !",
    hint_engineer: "Chaîne à 3 maillons : forge → moteur à vapeur → mur de glace. Trouve le déclencheur.",
    reward: { shards: 15, relic: "sprint-temporel", grimoire_page: 1 },
    eras: {
      antiquity: {
        objects: [],
        slots: [
          { id: "nile", label: "Barrage du Nil", initial: "ice", target: "flowing", 
            renders: { ice: "🧊 Mur de glace", flowing: "🌊 Nil libéré" } },
        ],
        goal_slot: "nile",
      },
      medieval: {
        objects: [{ id: "ember", label: "Braise ardente", symbol: "flame", color: "orange" }],
        slots: [
          { id: "forge", label: "Forge du forgeron", accepts: ["ember"], initial: "cold",
            renders: { cold: "Forge éteinte", lit: "🔥 Forge en feu" } },
        ],
      },
      future: {
        objects: [],
        slots: [
          { id: "steam_engine", label: "Moteur à vapeur cybernétique", initial: "off",
            renders: { off: "Moteur inerte", on: "⚡ Moteur activé" } },
        ],
      },
    },
    rules: [
      { when: { era: "medieval", slot: "forge", has: "ember" }, 
        then: { era: "medieval", slot: "forge", state: "lit" } },
      { when: { era: "medieval", slot: "forge", state: "lit" },
        then: { era: "future", slot: "steam_engine", state: "on" } },
      { when: { era: "future", slot: "steam_engine", state: "on" },
        then: { era: "antiquity", slot: "nile", state: "flowing" } },
    ],
  },

  {
    id: 2,
    title: "Le Poids des Époques",
    subtitle: "Chapitre II · L'engrenage oublié",
    hint_discovery: "Pose la pierre bleue dans le sanctuaire. Un engrenage médiéval tournera !",
    hint_engineer: "Deux chaînes parallèles à activer avant que la porte du futur s'ouvre.",
    reward: { shards: 20, relic: "magnetoscope", grimoire_page: 2 },
    eras: {
      antiquity: {
        objects: [{ id: "stone", label: "Pierre du Sphinx", symbol: "square", color: "amber" }],
        slots: [
          { id: "altar", label: "Autel du soleil", accepts: ["stone"], initial: "empty",
            renders: { empty: "Autel vide", pressed: "☀️ Autel activé" } },
        ],
      },
      medieval: {
        objects: [{ id: "lever", label: "Levier de fer", symbol: "wrench", color: "gray" }],
        slots: [
          { id: "gear", label: "Grand Engrenage", initial: "still",
            renders: { still: "Engrenage figé", spinning: "⚙️ Engrenage en marche" } },
          { id: "drawbridge", label: "Pont-levis", accepts: ["lever"], initial: "raised",
            renders: { raised: "Pont levé", lowered: "🌉 Pont baissé" } },
        ],
      },
      future: {
        objects: [],
        slots: [
          { id: "portal", label: "Portail du futur", initial: "closed",
            renders: { closed: "🚪 Portail scellé", open: "✨ Portail ouvert" } },
        ],
        goal_slot: "portal",
      },
    },
    rules: [
      { when: { era: "antiquity", slot: "altar", has: "stone" }, 
        then: { era: "antiquity", slot: "altar", state: "pressed" } },
      { when: { era: "antiquity", slot: "altar", state: "pressed" },
        then: { era: "medieval", slot: "gear", state: "spinning" } },
      { when: { era: "medieval", slot: "drawbridge", has: "lever" },
        then: { era: "medieval", slot: "drawbridge", state: "lowered" } },
      { when: { era: "medieval", slot: "gear", state: "spinning", and: { slot: "drawbridge", state: "lowered" } },
        then: { era: "future", slot: "portal", state: "open" } },
    ],
  },

  {
    id: 3,
    title: "La Chaîne Cybernétique",
    subtitle: "Chapitre III · Réactions croisées",
    hint_discovery: "Suis les couleurs : orange → vert → bleu → violet !",
    hint_engineer: "Quatre étapes en cascade. Pense à l'ordre : Antiquité → Médiéval → Futur → Antiquité.",
    reward: { shards: 30, relic: "aimant-quantique", grimoire_page: 3 },
    eras: {
      antiquity: {
        objects: [
          { id: "scarab", label: "Scarabée sacré", symbol: "bug", color: "emerald" },
        ],
        slots: [
          { id: "tomb", label: "Tombeau du Pharaon", accepts: ["scarab"], initial: "sealed",
            renders: { sealed: "🪦 Tombeau scellé", open: "📜 Tombeau ouvert" } },
          { id: "crystal", label: "Cristal solaire", initial: "dark",
            renders: { dark: "💎 Cristal éteint", bright: "✨ Cristal brillant" } },
        ],
        goal_slot: "crystal",
      },
      medieval: {
        objects: [],
        slots: [
          { id: "bell", label: "Cloche du beffroi", initial: "silent",
            renders: { silent: "🔔 Cloche muette", ringing: "🔔 Cloche résonnante" } },
        ],
      },
      future: {
        objects: [{ id: "laser", label: "Faisceau laser", symbol: "zap", color: "cyan" }],
        slots: [
          { id: "reflector", label: "Réflecteur quantique", accepts: ["laser"], initial: "off",
            renders: { off: "Réflecteur off", active: "🔷 Réflecteur actif" } },
        ],
      },
    },
    rules: [
      { when: { era: "antiquity", slot: "tomb", has: "scarab" },
        then: { era: "antiquity", slot: "tomb", state: "open" } },
      { when: { era: "antiquity", slot: "tomb", state: "open" },
        then: { era: "medieval", slot: "bell", state: "ringing" } },
      { when: { era: "future", slot: "reflector", has: "laser" },
        then: { era: "future", slot: "reflector", state: "active" } },
      { when: { era: "medieval", slot: "bell", state: "ringing", and: { era: "future", slot: "reflector", state: "active" } },
        then: { era: "antiquity", slot: "crystal", state: "bright" } },
    ],
  },

  {
    id: 4,
    title: "La Fissure Finale",
    subtitle: "Chapitre IV · Là où tout converge",
    hint_discovery: "Chaque époque a un objet. Place-les tous et observe la magie !",
    hint_engineer: "Assemble les 3 conditions simultanément pour refermer la fissure ultime.",
    reward: { shards: 50, relic: "horloge-de-luna", grimoire_page: 4 },
    eras: {
      antiquity: {
        objects: [{ id: "ankh", label: "Ankh d'or", symbol: "cross", color: "amber" }],
        slots: [
          { id: "obelisk", label: "Obélisque", accepts: ["ankh"], initial: "dim",
            renders: { dim: "Obélisque endormi", glowing: "🌟 Obélisque radieux" } },
        ],
      },
      medieval: {
        objects: [{ id: "sword", label: "Épée de vérité", symbol: "sword", color: "silver" }],
        slots: [
          { id: "anvil", label: "Enclume enchantée", accepts: ["sword"], initial: "cold",
            renders: { cold: "Enclume ordinaire", forged: "⚒️ Enclume forgée" } },
        ],
      },
      future: {
        objects: [{ id: "core", label: "Noyau plasma", symbol: "atom", color: "cyan" }],
        slots: [
          { id: "reactor", label: "Réacteur du temps", accepts: ["core"], initial: "cold",
            renders: { cold: "Réacteur inerte", online: "⚛️ Réacteur en ligne" } },
          { id: "fissure", label: "Fissure Temporelle", initial: "open",
            renders: { open: "☄️ Fissure ouverte", healed: "🌈 Fissure refermée" } },
        ],
        goal_slot: "fissure",
      },
    },
    rules: [
      { when: { era: "antiquity", slot: "obelisk", has: "ankh" },
        then: { era: "antiquity", slot: "obelisk", state: "glowing" } },
      { when: { era: "medieval", slot: "anvil", has: "sword" },
        then: { era: "medieval", slot: "anvil", state: "forged" } },
      { when: { era: "future", slot: "reactor", has: "core" },
        then: { era: "future", slot: "reactor", state: "online" } },
      { when: { era: "antiquity", slot: "obelisk", state: "glowing",
                and: { era: "medieval", slot: "anvil", state: "forged" },
                and2: { era: "future", slot: "reactor", state: "online" } },
        then: { era: "future", slot: "fissure", state: "healed" } },
    ],
  },

  {
    id: 5,
    title: "Le Théâtre du Temps",
    subtitle: "Chapitre V · Renaissance & Bonus",
    hint_discovery: "Lève le rideau du théâtre en plaçant la plume du dramaturge sur le pupitre !",
    hint_engineer: "Trois époques agissent en cascade sur la Renaissance, qui déclenche l'éclipse finale.",
    reward: { shards: 45, relic: "horloge-de-luna", grimoire_page: 5 },
    eras: {
      antiquity: {
        objects: [{ id: "sunmark", label: "Cadran solaire", symbol: "sun", color: "amber" }],
        slots: [
          { id: "gnomon", label: "Gnomon", accepts: ["sunmark"], initial: "empty",
            renders: { empty: "Gnomon nu", set: "☀️ Ombre projetée" } },
        ],
      },
      renaissance: {
        objects: [{ id: "quill", label: "Plume du dramaturge", symbol: "feather", color: "rose" }],
        slots: [
          { id: "podium", label: "Pupitre du théâtre", accepts: ["quill"], initial: "empty",
            renders: { empty: "Pupitre vide", signed: "📜 Manuscrit signé" } },
          { id: "curtain", label: "Rideau de scène", initial: "closed",
            renders: { closed: "🎭 Rideau baissé", raised: "🎭 Rideau levé" } },
        ],
      },
      future: {
        objects: [],
        slots: [
          { id: "eclipse", label: "Éclipse orchestrée", initial: "silent",
            renders: { silent: "🌑 Éclipse latente", brilliant: "🌟 Éclipse totale" } },
        ],
        goal_slot: "eclipse",
      },
    },
    rules: [
      { when: { era: "antiquity", slot: "gnomon", has: "sunmark" },
        then: { era: "antiquity", slot: "gnomon", state: "set" } },
      { when: { era: "renaissance", slot: "podium", has: "quill" },
        then: { era: "renaissance", slot: "podium", state: "signed" } },
      { when: { era: "renaissance", slot: "podium", state: "signed",
                and: { era: "antiquity", slot: "gnomon", state: "set" } },
        then: { era: "renaissance", slot: "curtain", state: "raised" } },
      { when: { era: "renaissance", slot: "curtain", state: "raised" },
        then: { era: "future", slot: "eclipse", state: "brilliant" } },
    ],
  },
];

// ============ ÉVEIL LEVELS (4-7 years old) ============
// Ultra-simple: 2 eras, 1 object, 1 slot, 1 causality step.
// Big emoji-first labels, no chain of 3, no puzzle wrong-answers possible.
export const EVEIL_LEVELS = [
  {
    id: 101,
    eveil: true,
    title: "La Braise et le Nil",
    subtitle: "Chapitre Éveil 1 · Chaud et froid",
    hint_discovery: "Prends la braise orange et pose-la sur la forge. Regarde la glace fondre !",
    hint_engineer: "Prends la braise orange et pose-la sur la forge. Regarde la glace fondre !",
    reward: { shards: 5, relic: "sprint-temporel", grimoire_page: 1 },
    eras: {
      medieval: {
        objects: [{ id: "ember", label: "🔥 Braise", symbol: "flame", color: "orange" }],
        slots: [
          { id: "forge", label: "Forge du forgeron", accepts: ["ember"], initial: "cold",
            renders: { cold: "❄️ Forge froide", lit: "🔥 Forge chaude !" } },
        ],
      },
      antiquity: {
        objects: [],
        slots: [
          { id: "nile", label: "Le Nil gelé", initial: "ice", target: "flowing",
            renders: { ice: "🧊 Rivière gelée", flowing: "🌊 Rivière libérée !" } },
        ],
        goal_slot: "nile",
      },
    },
    rules: [
      { when: { era: "medieval", slot: "forge", has: "ember" },
        then: { era: "medieval", slot: "forge", state: "lit" } },
      { when: { era: "medieval", slot: "forge", state: "lit" },
        then: { era: "antiquity", slot: "nile", state: "flowing" } },
    ],
  },
  {
    id: 102,
    eveil: true,
    title: "L'Étoile de Luna",
    subtitle: "Chapitre Éveil 2 · Lumière et jour",
    hint_discovery: "Prends l'étoile brillante et pose-la sur la lanterne. Le jour va se lever !",
    hint_engineer: "Prends l'étoile brillante et pose-la sur la lanterne. Le jour va se lever !",
    reward: { shards: 5, relic: "magnetoscope", grimoire_page: 2 },
    eras: {
      future: {
        objects: [{ id: "star", label: "⭐ Étoile", symbol: "star", color: "yellow" }],
        slots: [
          { id: "lantern", label: "Lanterne magique", accepts: ["star"], initial: "off",
            renders: { off: "🌑 Lanterne éteinte", on: "🌟 Lanterne allumée !" } },
        ],
      },
      antiquity: {
        objects: [],
        slots: [
          { id: "sky", label: "Le ciel du désert", initial: "night", target: "day",
            renders: { night: "🌃 Nuit noire", day: "☀️ Grand soleil !" } },
        ],
        goal_slot: "sky",
      },
    },
    rules: [
      { when: { era: "future", slot: "lantern", has: "star" },
        then: { era: "future", slot: "lantern", state: "on" } },
      { when: { era: "future", slot: "lantern", state: "on" },
        then: { era: "antiquity", slot: "sky", state: "day" } },
    ],
  },
];

export function getInitialState(level) {
  const state = {};
  for (const eraKey of Object.keys(level.eras)) {
    state[eraKey] = { slots: {} };
    for (const slot of level.eras[eraKey].slots) {
      state[eraKey].slots[slot.id] = { state: slot.initial, contents: null };
    }
  }
  return state;
}

// Evaluate a single rule condition given current state
function condMet(cond, state) {
  if (cond.era && cond.slot) {
    const s = state[cond.era]?.slots[cond.slot];
    if (!s) return false;
    if (cond.has && s.contents !== cond.has) return false;
    if (cond.state && s.state !== cond.state) return false;
    return true;
  }
  return false;
}

// Apply rules iteratively until stable
export function applyRules(level, state) {
  const next = JSON.parse(JSON.stringify(state));
  let changed = true;
  let safety = 20;
  while (changed && safety-- > 0) {
    changed = false;
    for (const rule of level.rules) {
      const primary = { era: rule.when.era, slot: rule.when.slot, has: rule.when.has, state: rule.when.state };
      const primaryOk = condMet(primary, next);
      let andOk = true;
      if (rule.when.and) andOk = condMet({ ...rule.when.and, era: rule.when.and.era || rule.when.era }, next);
      let and2Ok = true;
      if (rule.when.and2) and2Ok = condMet({ ...rule.when.and2, era: rule.when.and2.era || rule.when.era }, next);

      if (primaryOk && andOk && and2Ok) {
        const t = rule.then;
        const target = next[t.era]?.slots[t.slot];
        if (target && target.state !== t.state) {
          target.state = t.state;
          changed = true;
        }
      }
    }
  }
  return next;
}

export function isLevelSolved(level, state) {
  for (const eraKey of Object.keys(level.eras)) {
    const era = level.eras[eraKey];
    if (!era.goal_slot) continue;
    const goal = era.slots.find(s => s.id === era.goal_slot);
    if (!goal) continue;
    if (state[eraKey].slots[goal.id].state !== goal.target && state[eraKey].slots[goal.id].state === goal.initial) {
      return false;
    }
    // If we have explicit target
    if (goal.target && state[eraKey].slots[goal.id].state !== goal.target) return false;
  }
  return true;
}

// Find a level by id across regular and Éveil chapters
export function findLevel(id) {
  const numId = Number(id);
  return LEVELS.find((l) => l.id === numId) || EVEIL_LEVELS.find((l) => l.id === numId) || null;
}
